<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Contracts" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            v-if="!isReadOnlyAccess"
            icon="add"
            color="primary"
            text="Add"
            :routeName="assignmentCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <AssignmentFilters
        class="px-5 mb-sm-5"
        :jobTitles="jobTitles"
        :showDeactivateInBulk="showDeactivateInBulk"
        @search="refreshAssignments"
        @clear="refreshAssignments"
        @deactivateInBulk="handleDeactivateInBulkClick"
      ></AssignmentFilters>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="assignmentList"
          class="elevation-2"
        >
          <template v-slot:item.assignmentSelected="{ item }">
            <v-checkbox
              v-model="item.assignmentSelected"
              class="py-0"
            ></v-checkbox>
          </template>
          <template v-slot:item.startAt="{ item }">
            {{ item.startAt | formatDate }}
          </template>
          <template v-slot:item.endAt="{ item }">
            {{ item.endAt | formatDate }}
          </template>
          <template v-slot:item.active="{ item }">
            <v-checkbox
              v-model="item.active"
              class="py-0"
              disabled
            ></v-checkbox>
          </template>
          <template v-slot:item.overtimeEligible="{ item }">
            <v-simple-checkbox
              v-model="item.overtimeEligible"
              disabled
            ></v-simple-checkbox>
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              style="margin: 1px 1.5px"
              color="primary"
              outlined
              @click.prevent="editAssignment($event, item)"
            >
              <v-icon color="primary">{{ actionIcon }}</v-icon>
            </v-btn>
            <v-btn
              v-if="isAdmin"
              class="mx-1 my-1"
              color="red"
              outlined
              @click="deleteAssignmentHandler(item)"
            >
              <v-icon color="red">delete</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  style="margin: 1px 1.5px"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="showOneOffFeesRecordsListModal(item)"
                >
                  <v-icon color="primary">receipt_long</v-icon>
                </v-btn>
              </template>
              <span>Show One Off Fees Records</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <OneOffFeesRecordListModal
      :assignmentId="assignmentId"
      :userName="consultantName"
      :clientName="clientName"
      :showDateRangePicker="true"
      :readonly="isReadOnlyAccess"
      ref="OneOffFeesRecordListModal"
    >
    </OneOffFeesRecordListModal>
    <ConfirmationModal
      :title="'Delete Confirmation'"
      :contentText="'Are you sure you want to delete the selected contract?'"
      :toolbarColor="'primary'"
      @cancel-click="() => {
        this.showDeleteConfirmationDialog = false
        this.contractIdToDelete = null
      }"
      @ok-click="confirmDelete"
      :showModal="showDeleteConfirmationDialog"
    ></ConfirmationModal>
    <CloseAssignmentModal
      ref="closeAssignmentModal"
      :assignmentsToDeactivate="assignmentsToDeactivate"
      @assignmentsDeactivated="refreshAssignments"
    ></CloseAssignmentModal>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { assignmentCreateRoute, assignmentEditRoute } from '@/lib/constants/routes'
import AssignmentFilters from './AssignmentFilters'
import { BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { pageableAndSortable, hasQueryChanged } from '@/lib/pagingAndSorting'
import {
  ASSIGNMENT_FILTER_QUERY_KEYS,
  parseAssignmentFiltersFromQuery,
  buildAssignmentFiltersQuery,
  queryDescribesFilters
} from './assignmentListQueryParams'
import { ADMIN, MANAGER } from '@/lib/constants/securityRoles'
import { userIsAdmin, getCurrentUser, userHasRoles } from '@/lib/authentication'
import OneOffFeesRecordListModal from '@/components/oneOffFeesRecords/OneOffFeesRecordListModal'
import { ConfirmationModal } from '@/components/dialogs'
import CloseAssignmentModal from './CloseAssignmentModal'

export default {
  mixins: [
    pageableAndSortable
  ],
  components: {
    BottomSpacer,
    AssignmentFilters,
    ButtonCustomIcon,
    SimplePageHeader,
    OneOffFeesRecordListModal,
    ConfirmationModal,
    CloseAssignmentModal
  },
  data () {
    return {
      assignmentCreateRoute,
      assignmentInfo: {},
      assignmentId: null,
      consultantName: null,
      clientName: null,
      activeAssignment: null,
      jobTitles: [],
      showDeleteConfirmationDialog: false,
      contractIdToDelete: null
    }
  },
  computed: {
    ...mapFields('assignments', [
      'assignmentList'
    ]),
    ...mapFields('assignmentFilters', [
      'includeInactive',
      'clientId',
      'userId'
    ]),
    isReadOnlyAccess: {
      get () {
        return !this.userHasRoles({ user: this.getCurrentUser(), allowedRoles: [ADMIN, MANAGER] })
      }
    },
    actionIcon () {
      return this.isReadOnlyAccess ? 'visibility' : 'edit'
    },
    headers: {
      get () {
        return [
          ...(this.isAdmin
            ? [{ text: '', value: 'assignmentSelected', width: '5%', cellClass: 'px-0 pl-3' }]
            : []),

          { text: 'Client', value: 'clientName', width: '14%', cellClass: 'px-0 pl-3' },
          { text: 'Consultant', value: 'userFullName', width: '14%', cellClass: 'px-0 pl-2' },
          { text: 'Job Title', value: 'jobTitle', width: '14%', cellClass: 'px-0 pl-2' },
          { text: 'Start', value: 'startAt', align: 'center', width: '8%', class: 'hide-on-sm px-0', cellClass: 'hide-on-sm px-1' },
          { text: 'End', value: 'endAt', align: 'center', width: '8%', class: 'hide-on-sm px-0', cellClass: 'hide-on-sm px-1' },
          { text: 'Active', value: 'active', align: 'center', width: '5%', class: 'px-1' },
          {
            text: 'Overtime Eligible',
            value: 'overtimeEligible',
            align: 'center',
            class: 'hide-on-sm px-0',
            cellClass: 'hide-on-sm',
            width: '10%'
          },
          {
            text: 'Actions',
            value: 'action',
            align: 'center',
            sortable: false,
            width: '25%',
            class: 'px-0',
            cellClass: 'px-2'
          }
        ]
      }
    },
    isAdmin () {
      return userIsAdmin()
    },
    assignmentsToDeactivate () {
      return this.assignmentList.filter(i => i.assignmentSelected)
    },
    showDeactivateInBulk () {
      return this.assignmentsToDeactivate.length > 0 && this.isAdmin
    },
    appliedFilters () {
      return { clientId: this.clientId, userId: this.userId }
    }
  },
  methods: {
    userHasRoles,
    getCurrentUser,
    ...mapActions('assignments', [
      'getFieldsWithFilters'
    ]),
    ...mapActions('assignment', [
      'deleteAssignment'
    ]),
    ...mapActions('assignmentFilters', [
      'clearFilters'
    ]),
    editAssignment (event, assignment) {
      if (event.ctrlKey === true) {
        let route = this.$router.resolve({ name: assignmentEditRoute.name, params: { id: assignment.id } })
        window.open(route.href, '_blank')
      } else {
        this.$router.push({ name: assignmentEditRoute.name, params: { id: assignment.id } })
      }
    },
    handleDeactivateInBulkClick () {
      this.$refs.closeAssignmentModal.showModal()
    },
    showOneOffFeesRecordsListModal (item) {
      this.assignmentId = item.id
      this.consultantName = item.userFullName
      this.clientName = item.clientName
      this.activeAssignment = item.active
      this.$refs.OneOffFeesRecordListModal.showModal()
    },
    deleteAssignmentHandler (item) {
      this.contractIdToDelete = item.id
      this.showDeleteConfirmationDialog = true
    },
    confirmDelete () {
      this.deleteAssignment(this.contractIdToDelete).then(() => {
        this.showDeleteConfirmationDialog = false
        this.refreshAssignments()
      })
    },
    applyFiltersFromQuery () {
      const { clientId, userId } = parseAssignmentFiltersFromQuery(this.$route.query)

      // Only what the link actually says. The filters live in a store module that outlives this
      // screen, so overwriting the absent ones with null would clear filters a person set on their
      // last visit -- which is not what opening /assignments does today.
      if (clientId !== null) {
        this.clientId = clientId
      }

      if (userId !== null) {
        this.userId = userId
      }
    },
    syncFiltersToQuery () {
      const nextQuery = buildAssignmentFiltersQuery(this.$route.query, this.appliedFilters)

      if (hasQueryChanged(this.$route.query, nextQuery, ASSIGNMENT_FILTER_QUERY_KEYS)) {
        this.$router.replace({ query: nextQuery })
      }
    },
    refreshAssignments () {
      // Written here rather than on every keystroke in the filter bar, so the address bar always
      // describes the list actually on screen and can be copied as-is.
      this.syncFiltersToQuery()

      this.getFieldsWithFilters().then(() => {
        this.assignmentList = this.assignmentList.filter(
          (assignment) => (assignment.active || assignment.active === !this.includeInactive)
        )
        this.jobTitles = this.assignmentList.map((assignment) => { return { jobTitle: assignment.jobTitle ? assignment.jobTitle.trim() : null } })
        this.jobTitles = this.jobTitles.filter(item => {
          if (item.jobTitle !== null && item.jobTitle !== '') {
            return true
          }
          return false
        })
        this.jobTitles.sort((a, b) => (a.jobTitle > b.jobTitle) ? 1 : -1)
      })
    }
  },
  watch: {
    // A same-route query change does not recreate this component -- App.vue's router-view has no
    // :key -- so created() runs once and never again. Browser Back off a filtered link, and a second
    // notification link opened in a tab already sitting on this screen, both arrive here and nowhere
    // else. Without this the list stays filtered while the URL says otherwise, and the next refresh
    // writes the old parameters straight back.
    '$route.query' (nextQuery) {
      // Applying a filter rewrites the URL, so this fires on our own writes too. Those already
      // describe the applied filters, and reapplying them would loop.
      if (queryDescribesFilters(nextQuery, this.appliedFilters)) {
        return
      }

      // Unlike created(), the whole filter set is reset first. A URL that changes under a live screen
      // is a navigation, so it describes the list in full rather than adding two fields to whatever
      // was there: a link naming a client and a consultant must not land on an empty list because of
      // a job title left over from the previous search.
      this.clearFilters()
      this.applyFiltersFromQuery()
      this.refreshAssignments()
    }
  },
  created () {
    // Only what the link says, with the rest left alone -- see applyFiltersFromQuery. Arriving at
    // the screen keeps the filters from the last visit, which is what returning from an assignment
    // preserves.
    this.applyFiltersFromQuery()
    this.refreshAssignments()
  }
}
</script>
